import React from "react";
import { DataGrid, DataGridProps } from "@mui/x-data-grid";
import { Box, Typography, CircularProgress } from "@mui/material";

interface DataGridWrapperProps extends DataGridProps {
  isReady?: boolean;
  loadingMessage?: string;
}

/**
 * DataGrid 래퍼 - 안전한 초기화를 보장
 * MUI X DataGrid의 상태 초기화 문제를 방지합니다.
 */
const DataGridWrapper: React.FC<DataGridWrapperProps> = ({
  isReady = true,
  loadingMessage = "데이터를 준비하는 중...",
  paginationModel,
  rows = [],
  rowCount = 0,
  ...props
}) => {
  // DataGrid 강제 재마운트를 위한 상태
  const [mountKey, setMountKey] = React.useState(0);
  const [isDataGridReady, setIsDataGridReady] = React.useState(false);

  // 에러 발생 시 재마운트 트리거
  const handleRetry = React.useCallback(() => {
    setMountKey((prev) => prev + 1);
    setIsDataGridReady(false);
  }, []);

  // DataGrid 준비 상태 관리
  React.useEffect(() => {
    if (isReady && paginationModel && Array.isArray(rows)) {
      // 약간의 지연을 두어 상태가 완전히 안정화되도록 함
      const timer = setTimeout(() => {
        setIsDataGridReady(true);
      }, 100);

      return () => clearTimeout(timer);
    } else {
      setIsDataGridReady(false);
    }
  }, [isReady, paginationModel, rows, mountKey]);
  // 필수 props 검증 - 더 엄격한 검증
  const isPropsReady = React.useMemo(() => {
    const hasValidPagination =
      paginationModel &&
      typeof paginationModel.page === "number" &&
      paginationModel.page >= 0 &&
      typeof paginationModel.pageSize === "number" &&
      paginationModel.pageSize > 0;

    const hasValidData =
      Array.isArray(rows) && typeof rowCount === "number" && rowCount >= 0;

    return isReady && isDataGridReady && hasValidPagination && hasValidData;
  }, [isReady, isDataGridReady, paginationModel, rows, rowCount]);

  // 안전한 props 생성 - 기본값 보장 및 강제 재렌더링
  const safeProps = React.useMemo(() => {
    if (!isPropsReady) return null;

    // 기본 pagination 모델 보장
    const safePaginationModel = {
      page: Math.max(0, paginationModel?.page ?? 0),
      pageSize: Math.max(1, paginationModel?.pageSize ?? 20),
    };

    // 안전한 rows와 rowCount 보장
    const safeRows = Array.isArray(rows) ? rows : [];
    const safeRowCount = Math.max(0, rowCount || 0);

    // MUI DataGrid 내부 상태 문제 해결을 위한 추가 props
    const additionalProps = {
      // 내부 상태 초기화 강제 (mountKey 포함)
      key: `datagrid-${safePaginationModel.page}-${safePaginationModel.pageSize}-${safeRowCount}-${mountKey}`,

      // 안전한 기본값들
      hideFooter: false,
      hideFooterPagination: false,
      hideFooterSelectedRowCount: false,

      // 상태 관리 강제 초기화
      initialState: {
        pagination: {
          paginationModel: safePaginationModel,
        },
      },

      // 에러 방지를 위한 추가 설정
      disableVirtualization: false,
      getRowHeight: () => "auto",

      // MUI DataGrid 내부 상태 문제 완전 우회
      autoHeight: false,
      density: "standard",

      // 페이지네이션 관련 안전장치
      pageSizeOptions: props.pageSizeOptions || [10, 20, 50, 100],

      // 선택 관련 안전장치
      checkboxSelection: props.checkboxSelection || false,
      disableRowSelectionOnClick: props.disableRowSelectionOnClick || false,
    };

    return {
      ...props,
      ...additionalProps,
      rows: safeRows,
      rowCount: safeRowCount,
      paginationModel: safePaginationModel,
    };
  }, [isPropsReady, props, rows, rowCount, paginationModel]);

  // 로딩 상태 표시
  if (!isPropsReady || !safeProps) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 400,
          flexDirection: "column",
          gap: 2,
          p: 3,
          bgcolor: "background.paper",
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          {loadingMessage}
        </Typography>
      </Box>
    );
  }

  // ErrorBoundary와 추가 안전장치로 DataGrid 에러 방지
  return (
    <ErrorBoundary
      onRetry={handleRetry}
      fallback={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 400,
            flexDirection: "column",
            gap: 2,
            p: 3,
          }}
        >
          <Typography variant="h6" color="error">
            데이터 그리드 로딩 중 문제가 발생했습니다
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            다시 시도하거나 페이지를 새로고침해주세요
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <button
              onClick={handleRetry}
              style={{
                padding: "8px 16px",
                backgroundColor: "#1976d2",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              다시 시도
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "8px 16px",
                backgroundColor: "#757575",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              새로고침
            </button>
          </Box>
        </Box>
      }
    >
      <React.Suspense
        fallback={
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        }
      >
        {/* 최종 안전장치: 모든 조건이 만족될 때만 DataGrid 렌더링 */}
        {safeProps &&
        safeProps.rows &&
        safeProps.paginationModel &&
        typeof safeProps.rowCount === "number" ? (
          <DataGrid {...safeProps} />
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              데이터 그리드를 초기화하는 중...
            </Typography>
          </Box>
        )}
      </React.Suspense>
    </ErrorBoundary>
  );
};

// 재시도 기능이 있는 ErrorBoundary 컴포넌트
class ErrorBoundary extends React.Component<
  {
    children: React.ReactNode;
    fallback: React.ReactNode;
    onRetry?: () => void;
  },
  { hasError: boolean; errorCount: number }
> {
  constructor(props: {
    children: React.ReactNode;
    fallback: React.ReactNode;
    onRetry?: () => void;
  }) {
    super(props);
    this.state = { hasError: false, errorCount: 0 };
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[DataGrid Error Boundary]", {
      error: error.message,
      stack: error.stack,
      errorInfo,
      errorCount: this.state.errorCount + 1,
      timestamp: new Date().toISOString(),
    });

    this.setState((prevState) => ({
      errorCount: prevState.errorCount + 1,
    }));
  }

  componentDidUpdate(prevProps: any) {
    // props가 변경되면 에러 상태 리셋 (재시도 시)
    if (prevProps.children !== this.props.children && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

export default DataGridWrapper;
